import React, {FC, ReactElement} from 'react';
import {
  BtnDisabled,
  BtnFilled,
  BtnOutlined,
  ButtonWrapper,
  ImageWrapper,
  BtnText,
} from './Button.styles';

export interface ButtonProps {
  title?: string;
  onClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  variant?: 'outlined' | 'filled' | 'text';
  size?: 'big' | 'regular' | 'small';
  type?: 'button' | 'submit' | 'reset';
  disabled?: boolean;
  iconSrc?: string;
  iconElement?: ReactElement;
  noWrap?: boolean;
  link?: string;
  openLinkBlank?: boolean;
  iconSrcSizes?: {width: number; height: number};
  active?: boolean;
  iconPosition?: 'left' | 'right';
  iconMargin?: string;
  fullWidth?: boolean;
  redButton?: boolean;
}

const Button: FC<ButtonProps> = ({
  title,
  onClick,
  fullWidth,
  variant = 'outlined',
  type = 'button',
  size = 'regular',
  noWrap = true,
  disabled,
  iconSrc,
  iconElement,
  link,
  openLinkBlank = false,
  iconSrcSizes = {width: 15, height: 16},
  active,
  iconPosition = 'right',
  iconMargin,
  redButton = false,
}) => {
  const renderButton = (variant: string) => {
    switch (variant) {
      case 'outlined':
        return (
          <BtnOutlined
            onClick={onClick}
            fullWidth={fullWidth}
            type={type}
            size={size}
            noWrap={noWrap}
            redButton={redButton}>
            {iconSrc && iconPosition === 'left' && (
              <ImageWrapper iconSrcSizes={iconSrcSizes} margin={iconMargin}>
                <img
                  src={iconSrc}
                  alt={'icon'}
                  width={iconSrcSizes.width}
                  height={iconSrcSizes.height}
                />
              </ImageWrapper>
            )}
            {iconElement && iconPosition === 'left' && (
              <ImageWrapper iconSrcSizes={iconSrcSizes} margin={iconMargin}>
                {iconElement}
              </ImageWrapper>
            )}
            {title}
            {iconSrc && iconPosition === 'right' && (
              <ImageWrapper iconSrcSizes={iconSrcSizes} margin={iconMargin}>
                <img
                  src={iconSrc}
                  alt={'icon'}
                  width={iconSrcSizes.width}
                  height={iconSrcSizes.height}
                />
              </ImageWrapper>
            )}
            {iconElement && iconPosition === 'right' && (
              <ImageWrapper iconSrcSizes={iconSrcSizes} margin={iconMargin}>
                {iconElement}
              </ImageWrapper>
            )}
          </BtnOutlined>
        );
      case 'filled':
        return (
          <>
            <BtnFilled
              onClick={onClick}
              type={type}
              size={size}
              fullWidth={fullWidth}
              noWrap={noWrap}
              active={active}
              redButton={redButton}>
              {iconSrc && iconPosition === 'left' && (
                <ImageWrapper iconSrcSizes={iconSrcSizes} margin={iconMargin}>
                  <img
                    src={iconSrc}
                    alt={'icon'}
                    width={iconSrcSizes.width}
                    height={iconSrcSizes.height}
                  />
                </ImageWrapper>
              )}
              {iconElement && iconPosition === 'left' && (
                <ImageWrapper iconSrcSizes={iconSrcSizes} margin={iconMargin}>
                  {iconElement}
                </ImageWrapper>
              )}
              {title}
              {iconSrc && iconPosition === 'right' && (
                <ImageWrapper iconSrcSizes={iconSrcSizes} margin={iconMargin}>
                  <img
                    src={iconSrc}
                    alt={'icon'}
                    width={iconSrcSizes.width}
                    height={iconSrcSizes.height}
                  />
                </ImageWrapper>
              )}
              {iconElement && iconPosition === 'right' && (
                <ImageWrapper iconSrcSizes={iconSrcSizes} margin={iconMargin}>
                  {iconElement}
                </ImageWrapper>
              )}
            </BtnFilled>
          </>
        );
      case 'text':
        return (
          <>
            <BtnText
              onClick={onClick}
              type={type}
              size={size}
              fullWidth={fullWidth}
              noWrap={noWrap}
              active={active}>
              {iconSrc && iconPosition === 'left' && (
                <ImageWrapper iconSrcSizes={iconSrcSizes} margin={iconMargin}>
                  <img
                    src={iconSrc}
                    alt={'icon'}
                    width={iconSrcSizes.width}
                    height={iconSrcSizes.height}
                  />
                </ImageWrapper>
              )}
              {iconElement && iconPosition === 'left' && (
                <ImageWrapper iconSrcSizes={iconSrcSizes} margin={iconMargin}>
                  {iconElement}
                </ImageWrapper>
              )}
              {title}
              {iconSrc && iconPosition === 'right' && (
                <ImageWrapper iconSrcSizes={iconSrcSizes} margin={iconMargin}>
                  <img
                    src={iconSrc}
                    alt={'icon'}
                    width={iconSrcSizes.width}
                    height={iconSrcSizes.height}
                  />
                </ImageWrapper>
              )}
              {iconElement && iconPosition === 'right' && (
                <ImageWrapper iconSrcSizes={iconSrcSizes} margin={iconMargin}>
                  {iconElement}
                </ImageWrapper>
              )}
            </BtnText>
          </>
        );
    }
  };
  return (
    <>
      {!disabled ? (
        <>
          {link ? (
            <ButtonWrapper>
              <a
                href={link.trim()}
                target={openLinkBlank ? '_blank' : ''}
                rel="noreferrer">
                {renderButton(variant)}
              </a>
            </ButtonWrapper>
          ) : (
            <>{renderButton(variant)}</>
          )}
        </>
      ) : (
        <BtnDisabled
          onClick={onClick}
          variant={variant}
          type={type}
          size={size}
          fullWidth={fullWidth}
          noWrap={noWrap}>
          {iconSrc && iconPosition === 'left' && (
            <ImageWrapper iconSrcSizes={iconSrcSizes} margin={iconMargin}>
              <img
                src={iconSrc}
                alt={'icon'}
                width={iconSrcSizes.width}
                height={iconSrcSizes.height}
              />
            </ImageWrapper>
          )}
          {iconElement && iconPosition === 'left' && (
            <ImageWrapper iconSrcSizes={iconSrcSizes} margin={iconMargin}>
              {iconElement}
            </ImageWrapper>
          )}
          {title}
          {iconSrc && iconPosition === 'right' && (
            <ImageWrapper iconSrcSizes={iconSrcSizes} margin={iconMargin}>
              <img
                src={iconSrc}
                alt={'icon'}
                width={iconSrcSizes.width}
                height={iconSrcSizes.height}
              />
            </ImageWrapper>
          )}
          {iconElement && iconPosition === 'right' && (
            <ImageWrapper iconSrcSizes={iconSrcSizes} margin={iconMargin}>
              {iconElement}
            </ImageWrapper>
          )}
        </BtnDisabled>
      )}
    </>
  );
};

export default Button;
